/**
 * Prestige Theme - Premium Shopify Theme for Luxury Brands
 * Author: Michael Benjamin Crowe
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // Simple i18n helpers
  const STR = (window.theme && window.theme.strings) || {};
  const s = (key, fallback = '') => (typeof STR[key] === 'string' && STR[key]) || fallback || key;
  const interpolate = (str, ...vals) => typeof str === 'string' ? str.replace(/%s/g, () => vals.shift() ?? '') : undefined;
  const replacePlaceholders = (str, map) => {
    if (typeof str !== 'string') return undefined;
    return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in map ? map[key] : m));
  };
  const escapeHtml = (unsafe) => String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const Prestige = {
    settings: {
      animationDuration: 400,
      debounceDelay: 300,
      cartType: window.theme?.cartType || 'drawer'
    },

    init() {
      this.initHeader();
      this.initCart();
      this.initProductForms();
      this.initProductMedia();
      this.initModals();
      this.initSearch();
      this.initNewsletter();
      this.initWishlist();
      this.initQuickShop();
      this.initCollectionFilters();
      this.initAnimations();
      this.initAccessibility();
    },

    initHeader() {
      const header = document.querySelector('.header');
      if (!header) return;

      const stickyHeader = header.classList.contains('header--sticky');
      const transparentHeader = header.classList.contains('header--transparent');

      if (stickyHeader) {
        let lastScroll = 0;
        window.addEventListener('scroll', this.debounce(() => {
          const currentScroll = window.pageYOffset;

          if (currentScroll > 100) {
            header.classList.add('is-scrolled');

            if (currentScroll > lastScroll && currentScroll > 300) {
              header.classList.add('is-hidden');
            } else {
              header.classList.remove('is-hidden');
            }
          } else {
            header.classList.remove('is-scrolled');
            header.classList.remove('is-hidden');
          }

          lastScroll = currentScroll;
        }, 10));
      }

      if (transparentHeader) {
        window.addEventListener('scroll', () => {
          if (window.pageYOffset > 50) {
            header.classList.add('is-filled');
          } else {
            header.classList.remove('is-filled');
          }
        });
      }

      // Mobile navigation drawer is managed by sections/header.liquid
    },

    initCart() {
      this.cartDrawer = document.querySelector('.cart-drawer');
      this.cartCount = document.querySelectorAll('[data-cart-count]');

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-toggle]')) {
          e.preventDefault();
          this.toggleCart();
          this.updateCart();
          return;
        }

        if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) {
          e.preventDefault();
          this.closeCart();
          return;
        }

        // Cart drawer item remove
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          const itemKey = removeBtn.getAttribute('data-cart-remove');
          this.changeItemQuantity(itemKey, 0);
          return;
        }

        // Cart drawer quantity decrease
        const decBtn = e.target.closest('.cart-drawer [data-quantity-decrease]');
        if (decBtn) {
          e.preventDefault();
          const itemKey = decBtn.getAttribute('data-item-key');
          const input = decBtn.parentElement ? decBtn.parentElement.querySelector('input[data-quantity-input]') : null;
          const currentVal = parseInt(input ? input.value : 1) || 1;
          const newVal = Math.max(0, currentVal - 1);
          this.changeItemQuantity(itemKey, newVal);
          return;
        }

        // Cart drawer quantity increase
        const incBtn = e.target.closest('.cart-drawer [data-quantity-increase]');
        if (incBtn) {
          e.preventDefault();
          const itemKey = incBtn.getAttribute('data-item-key');
          const input = incBtn.parentElement ? incBtn.parentElement.querySelector('input[data-quantity-input]') : null;
          const currentVal = parseInt(input ? input.value : 1) || 1;
          this.changeItemQuantity(itemKey, currentVal + 1);
          return;
        }
      });

      // Cart drawer manual quantity input change
      document.addEventListener('change', (e) => {
        if (e.target.matches('.cart-drawer [data-quantity-input]')) {
          const itemKey = e.target.getAttribute('data-item-key');
          const newVal = parseInt(e.target.value) || 1;
          this.changeItemQuantity(itemKey, Math.max(0, newVal));
        }
      });

      // Cart drawer order note autosave
      document.addEventListener('input', this.debounce(async (e) => {
        if (e.target && e.target.id === 'CartDrawerNote') {
          const noteVal = e.target.value;
          const indicator = document.getElementById('CartDrawerNoteSaved');
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteVal })
            });
            if (indicator) {
              indicator.style.display = 'inline-block';
              setTimeout(() => { indicator.style.display = 'none'; }, 2000);
            }
          } catch (err) {
            console.warn('Note save error:', err);
          }
        }
      }, 500));

      document.addEventListener('cart:updated', () => {
        this.updateCart();
      });
    },

    async changeItemQuantity(key, quantity) {
      if (!key) return;
      if (this._isUpdatingCart) return;
      this._isUpdatingCart = true;
      const drawer = this.cartDrawer || document.querySelector('.cart-drawer');
      if (drawer) drawer.classList.add('is-loading-items');

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        if (response.ok) {
          const cart = await response.json();
          this.updateCartCount(cart.item_count);
          this.updateCartDrawer(cart);
          if (cart.item_count === 0) {
            this.showEmptyCart();
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        }
      } catch (err) {
        console.error('Error changing cart quantity:', err);
      } finally {
        this._isUpdatingCart = false;
        if (drawer) drawer.classList.remove('is-loading-items');
      }
    },

    toggleCart() {
      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (this.cartDrawer) {
        const isOpen = this.cartDrawer.classList.contains('is-open');
        if (isOpen) {
          this.closeCart();
        } else {
          this.openCart();
        }
      }
    },

    openCart() {
      const mobileDrawer = document.querySelector('[data-mobile-menu]');
      const mobileOverlay = document.querySelector('[data-mobile-menu-overlay]');
      if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
        mobileDrawer.classList.remove('is-open');
        if (mobileOverlay) mobileOverlay.classList.remove('is-active');
        document.body.classList.remove('mobile-menu-active');
      }

      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (this.cartDrawer) {
        this.lastFocusedElement = document.activeElement;
        this.cartDrawer.classList.add('is-open');
        document.body.classList.add('cart-open');

        const overlay = document.querySelector('[data-cart-overlay]');
        if (overlay) {
          overlay.style.display = 'block';
        }

        const focusableElements = this.cartDrawer.querySelectorAll(
          'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length) {
          focusableElements[0].focus();
        }

        this.setupFocusTrap(this.cartDrawer);
        this.updateCart();
      }
    },

    closeCart() {
      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (this.cartDrawer) {
        this.cartDrawer.classList.remove('is-open');
        document.body.classList.remove('cart-open');

        const overlay = document.querySelector('[data-cart-overlay]');
        if (overlay) {
          overlay.style.display = 'none';
        }

        this.removeFocusTrap(this.cartDrawer);

        if (this.lastFocusedElement) {
          this.lastFocusedElement.focus();
        }
      }
    },

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.updateCartCount(cart.item_count);
        this.updateCartDrawer(cart);

        if (cart.item_count === 0) {
          this.showEmptyCart();
        }
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    },

    updateCartCount(count) {
      if (count === undefined) {
        this.updateCart();
        return;
      }
      const counts = document.querySelectorAll('[data-cart-count]');
      counts.forEach(element => {
        element.textContent = count;
        if (element.classList.contains('site-header__cart-count')) {
          element.style.display = count > 0 ? 'inline-flex' : 'none';
        }
        element.classList.remove('badge-pulse');
        void element.offsetWidth;
        element.classList.add('badge-pulse');
      });
    },

    updateCartDrawer(cart) {
      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (!this.cartDrawer) return;

      const itemsContainer = this.cartDrawer.querySelector('[data-cart-items]');
      const subtotalElement = this.cartDrawer.querySelector('[data-cart-subtotal]');
      const countBadge = this.cartDrawer.querySelector('[data-cart-count]');
      const footer = this.cartDrawer.querySelector('.cart-drawer__footer');

      if (countBadge) {
        countBadge.textContent = cart.item_count || 0;
      }

      const upsellWrap = this.cartDrawer.querySelector('#CartDrawerUpsell');
      const noteWrap = this.cartDrawer.querySelector('.cart-drawer__note-wrap');

      if (!cart.items || cart.items.length === 0) {
        this.showEmptyCart();
      } else {
        if (itemsContainer) {
          itemsContainer.innerHTML = cart.items.map(item => this.cartItemTemplate(item)).join('');
        }
        if (footer) {
          footer.style.display = 'block';
        }
        if (upsellWrap) {
          upsellWrap.style.display = '';
        }
        if (noteWrap) {
          noteWrap.style.display = '';
        }
      }

      if (subtotalElement) {
        subtotalElement.textContent = this.formatMoney(cart.total_price);
      }

      // Update VIP reward progress bar dynamically
      this.updateFreeShippingBar(cart.total_price);

      // Update WhatsApp order link dynamically
      const waBtn = this.cartDrawer.querySelector('#CartDrawerWhatsAppBtn');
      if (waBtn && cart.items && cart.items.length > 0) {
        const itemSummary = cart.items.map(i => {
          const varText = (i.variant_title && i.variant_title !== 'Default Title') ? ` (${i.variant_title})` : '';
          return `${i.quantity}x ${i.product_title}${varText}`;
        }).join(', ');
        const total = this.formatMoney(cart.total_price);
        const waMsg = encodeURIComponent(`Assalam-o-Alaikum Habibi Arts! Mujhe apna order confirm karna hai:\n\n📦 Artworks: ${itemSummary}\n💰 Total: ${total} (Free Delivery Nationwide)\n\nMeherbani farma kar mera order confirm karein.`);
        waBtn.href = `https://wa.me/923274485859?text=${waMsg}`;
      }

      window._currentCartTotal = cart.total_price;
      window._currentCartItems = cart.items;
    },

    cartItemTemplate(item) {
      let imgUrl = (item.featured_image && item.featured_image.url) ? item.featured_image.url : (item.image || '');
      if (imgUrl) {
        imgUrl += (imgUrl.includes('?') ? '&width=200' : '?width=200');
      }
      const itemPrice = item.final_line_price !== undefined ? item.final_line_price : (item.price * item.quantity);
      const variantDisplay = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</p>` : '';
      const itemUrl = item.url || '/collections/all';
      const itemTitle = escapeHtml(item.product_title || item.title || 'Fine Art Print');

      return `
        <div class="cart-drawer__item" data-cart-item="${item.key}">
          <a href="${itemUrl}" class="cart-drawer__item-image">
            ${imgUrl ? `<img src="${imgUrl}" alt="${itemTitle}" width="72" height="80" loading="lazy">` : ''}
          </a>
          <div class="cart-drawer__item-details">
            <a href="${itemUrl}" class="cart-drawer__item-title">${itemTitle}</a>
            ${variantDisplay}
            <div class="cart-drawer__item-price-row">
              <span class="cart-drawer__item-price">${this.formatMoney(itemPrice)}</span>
            </div>
            <div class="cart-drawer__item-actions">
              <div class="cart-drawer__item-quantity">
                <button type="button" data-quantity-decrease data-item-key="${item.key}" aria-label="${s('decreaseQuantity', 'Decrease quantity')}">−</button>
                <input type="number" value="${item.quantity}" min="1" data-quantity-input data-item-key="${item.key}" aria-label="Quantity">
                <button type="button" data-quantity-increase data-item-key="${item.key}" aria-label="${s('increaseQuantity', 'Increase quantity')}">+</button>
              </div>
              <button type="button" class="cart-drawer__item-remove" data-cart-remove="${item.key}" aria-label="${s('cartRemove', 'Remove')}">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                <span>${s('cartRemove', 'Remove')}</span>
              </button>
            </div>
          </div>
        </div>
      `;
    },

    showEmptyCart() {
      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (!this.cartDrawer) return;

      const itemsContainer = this.cartDrawer.querySelector('[data-cart-items]');
      if (itemsContainer) {
        itemsContainer.innerHTML = `
          <div class="cart-drawer__empty-state">
            <div class="cart-drawer__empty-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#FAF5E5" opacity="0.6" stroke-width="1.8">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
            </div>
            <h3 class="cart-drawer__empty-title">${s('cartEmpty', 'Your shopping bag is empty')}</h3>
            <p class="cart-drawer__empty-subtitle">Discover our museum-quality fine art prints and find the perfect masterpiece for your walls.</p>
            <a href="/collections/all" class="button cart-drawer__empty-btn">Explore The Collection</a>

            <div class="cart-drawer__empty-quicklinks">
              <span class="quicklinks-label">Popular Categories:</span>
              <div class="quicklinks-pills">
                <a href="/collections/all/calligraphy">✨ Calligraphy</a>
                <a href="/collections/all/heritage-art">🏛️ Heritage Art</a>
                <a href="/collections/all/wild-life">🐎 Wildlife</a>
              </div>
            </div>
          </div>
        `;
      }

      const footer = this.cartDrawer.querySelector('.cart-drawer__footer');
      if (footer) {
        footer.style.display = 'none';
      }
      const upsellWrap = this.cartDrawer.querySelector('#CartDrawerUpsell');
      if (upsellWrap) {
        upsellWrap.style.display = 'none';
      }
      const noteWrap = this.cartDrawer.querySelector('.cart-drawer__note-wrap');
      if (noteWrap) {
        noteWrap.style.display = 'none';
      }
    },

    recalculateDrawerTotal() {
      if (!this.cartDrawer) {
        this.cartDrawer = document.querySelector('.cart-drawer');
      }
      if (!this.cartDrawer) return;

      const subtotalElement = this.cartDrawer.querySelector('[data-cart-subtotal]');
      if (subtotalElement && window._currentCartTotal !== undefined) {
        subtotalElement.textContent = this.formatMoney(window._currentCartTotal);
      }
    },

    updateFreeShippingBar(totalPrice) {
      // Gamified Reward & Free Delivery Progress Bar
      const rewardBar = document.querySelector('[data-reward-bar]');
      if (rewardBar) {
        const threshold = parseInt(rewardBar.dataset.threshold || '550000', 10);
        const remaining = threshold - totalPrice;
        const fill = rewardBar.querySelector('[data-reward-fill]');
        const text = rewardBar.querySelector('[data-reward-text]');

        if (remaining > 0) {
          const pct = Math.min(100, Math.max(0, (totalPrice / threshold) * 100));
          if (fill) fill.style.width = `${pct}%`;
          if (text) text.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more to unlock <strong>Free Luxury Velvet Box!</strong> 🎁`;
        } else {
          if (fill) fill.style.width = '100%';
          if (text) text.innerHTML = `🎉 <strong>VIP Unlocked:</strong> Free Delivery & Luxury Velvet Gift Box!`;
        }
      }

      // Legacy fallback support
      const freeShippingBar = document.querySelector('[data-free-shipping-bar]');
      if (freeShippingBar) {
        const threshold = parseInt(freeShippingBar.dataset.threshold) * 100;
        const remaining = threshold - totalPrice;
        if (remaining > 0) {
          const percentage = (totalPrice / threshold) * 100;
          freeShippingBar.querySelector('[data-progress]').style.width = `${percentage}%`;
        } else {
          freeShippingBar.querySelector('[data-progress]').style.width = '100%';
        }
      }
    },

    initProductForms() {
      document.addEventListener('submit', async (e) => {
        if (e.target.matches('[data-product-form]')) {
          e.preventDefault();
          await this.handleAddToCart(e.target);
        }
      });

      document.addEventListener('change', (e) => {
        if (e.target.matches('[data-variant-select]')) {
          this.handleVariantChange(e.target);
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-option-value]')) {
          e.preventDefault();
          this.handleOptionClick(e.target);
        }

        if (e.target.matches('[data-quantity-decrease]')) {
          this.decreaseQuantity(e.target);
        }

        if (e.target.matches('[data-quantity-increase]')) {
          this.increaseQuantity(e.target);
        }
      });
    },

    async handleAddToCart(form) {
      const submitButton = form.querySelector('[type="submit"]');
      const originalText = submitButton.textContent;

      submitButton.textContent = s('adding', 'Adding...');
      submitButton.disabled = true;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const item = await response.json();

          submitButton.textContent = s('added', 'Added!');

          await this.updateCart();

          if (this.settings.cartType === 'drawer') {
            this.toggleCart();
          }

          setTimeout(() => {
            submitButton.textContent = originalText;
            submitButton.disabled = false;
          }, 2000);

          document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        submitButton.textContent = s('error', 'Error');

        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
        }, 2000);
      }
    },

    handleVariantChange(select) {
      const form = select.closest('form');
      const selectedOption = select.options[select.selectedIndex];

      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      const available = selectedOption.dataset.available === 'true';
      const image = selectedOption.dataset.image;

      this.updateProductPrice(form, price, comparePrice);
      this.updateProductAvailability(form, available);

      if (image) {
        this.updateProductImage(image);
      }
    },

    handleOptionClick(option) {
      const group = option.closest('[data-option-group]');

      group.querySelectorAll('[data-option-value]').forEach(opt => {
        opt.classList.remove('is-active');
      });

      option.classList.add('is-active');

      const form = option.closest('form');
      const optionInputs = form.querySelectorAll('[data-option-input]');
      const optionName = option.dataset.optionName;
      const optionValue = option.dataset.optionValue;

      optionInputs.forEach(input => {
        if (input.name === optionName) {
          input.value = optionValue;
        }
      });

      this.updateSelectedVariant(form);
    },

    updateSelectedVariant(form) {
      const options = [];
      form.querySelectorAll('[data-option-input]').forEach(input => {
        options.push(input.value);
      });

      const variantTitle = options.join(' / ');
      const variantSelect = form.querySelector('[data-variant-select]');

      if (variantSelect) {
        const matchingOption = Array.from(variantSelect.options).find(option =>
          option.text === variantTitle
        );

        if (matchingOption) {
          variantSelect.value = matchingOption.value;
          this.handleVariantChange(variantSelect);
        }
      }
    },

    updateProductPrice(form, price, comparePrice) {
      const priceElement = form.closest('.product').querySelector('[data-product-price]');

      if (priceElement) {
        let priceHTML = this.formatMoney(price);

        if (comparePrice && comparePrice > price) {
          priceHTML = `
            <span class="product__price--sale">${this.formatMoney(price)}</span>
            <span class="product__price--compare">${this.formatMoney(comparePrice)}</span>
          `;
        }

        priceElement.innerHTML = priceHTML;
      }
    },

    updateProductAvailability(form, available) {
      const submitButton = form.querySelector('[type="submit"]');

      if (submitButton) {
        submitButton.disabled = !available;
        submitButton.textContent = available ? s('addToCart', 'Add to cart') : s('soldOut', 'Sold out');
      }
    },

    updateProductImage(imageSrc) {
      const mainImage = document.querySelector('[data-product-main-image]');

      if (mainImage) {
        mainImage.src = imageSrc;
      }
    },

    decreaseQuantity(button) {
      const input = button.parentElement.querySelector('input[type="number"]');
      const currentValue = parseInt(input.value);

      if (currentValue > 1) {
        input.value = currentValue - 1;
        input.dispatchEvent(new Event('change'));
      }
    },

    increaseQuantity(button) {
      const input = button.parentElement.querySelector('input[type="number"]');
      const currentValue = parseInt(input.value);

      input.value = currentValue + 1;
      input.dispatchEvent(new Event('change'));
    },

    initProductMedia() {
      const productImages = document.querySelectorAll('[data-product-image]');
      const thumbnails = document.querySelectorAll('[data-product-thumbnail]');

      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', (e) => {
          e.preventDefault();

          const imageSrc = thumbnail.dataset.imageSrc;
          const mainImage = document.querySelector('[data-product-main-image]');

          if (mainImage) {
            mainImage.src = imageSrc;
          }

          thumbnails.forEach(thumb => thumb.classList.remove('is-active'));
          thumbnail.classList.add('is-active');
        });
      });

      productImages.forEach(image => {
        image.addEventListener('click', () => {
          this.openImageZoom(image.src);
        });

        image.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openImageZoom(image.src);
          }
        });
      });
    },

    openImageZoom(imageSrc) {
      const modal = document.createElement('div');
      modal.className = 'modal image-zoom-modal is-open';
      modal.innerHTML = `
        <div class="modal__inner">
          <button class="modal__close" data-modal-close>&times;</button>
          <img src="${imageSrc}" alt="${s('zoomedImageAlt', 'Zoomed product image')}">
        </div>
      `;

      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.matches('[data-modal-close]')) {
          modal.remove();
        }
      });
    },

    initModals() {
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-modal-open]')) {
          e.preventDefault();
          const modalId = e.target.dataset.modalOpen;
          this.openModal(modalId);
        }

        if (e.target.matches('[data-modal-close]')) {
          e.preventDefault();
          this.closeModal(e.target.closest('.modal'));
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModals = document.querySelectorAll('.modal.is-open');
          openModals.forEach(modal => this.closeModal(modal));
        }
      });
    },

    openModal(modalId) {
      const modal = document.querySelector(`[data-modal="${modalId}"]`);

      if (modal) {
        // Store the currently focused element for restoration
        this.lastFocusedElement = document.activeElement;

        modal.classList.add('is-open');
        document.body.classList.add('modal-open');

        const focusableElements = modal.querySelectorAll(
          'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length) {
          focusableElements[0].focus();
        }

        // Add focus trapping
        this.setupFocusTrap(modal);
      }
    },

    closeModal(param) {
      let modal = null;
      if (param && param.nodeType === 1) {
        modal = param;
      } else if (typeof param === 'string' && param.trim()) {
        modal = document.querySelector(`[data-modal="${param}"]`) || document.querySelector(`[data-${param}-modal]`);
      } else {
        modal = document.querySelector('.modal.is-open');
      }

      if (modal) {
        modal.classList.remove('is-open');
        document.body.classList.remove('modal-open');

        // Remove focus trap
        this.removeFocusTrap(modal);

        // Restore focus to the last focused element
        if (this.lastFocusedElement) {
          try {
            this.lastFocusedElement.focus();
          } catch (e) {}
        }
      }
    },

    setupFocusTrap(modal) {
      const focusableElements = modal.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      modal.addEventListener('keydown', handleTabKey);
      modal._focusTrapHandler = handleTabKey;
    },

    removeFocusTrap(modal) {
      if (modal._focusTrapHandler) {
        modal.removeEventListener('keydown', modal._focusTrapHandler);
        delete modal._focusTrapHandler;
      }
    },

    trapFocus(modal) {
      this.setupFocusTrap(modal);
    },

    openModal(name) {
      const modal = document.querySelector(`[data-modal="${name}"]`) || document.querySelector(`[data-${name}-modal]`);
      if (modal) {
        modal.classList.add('is-open');
        document.body.classList.add('modal-open');
        this.setupFocusTrap(modal);
        const input = modal.querySelector('input[type="search"], input');
        if (input) setTimeout(() => input.focus(), 150);
      }
    },


    initSearch() {
      const searchModal = document.querySelector('[data-search-modal]');
      const searchForm = document.querySelector('[data-search-form]');
      const searchInput = document.querySelector('[data-search-input]');
      const searchResults = document.querySelector('[data-search-results]');

      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-search-toggle]');
        if (toggle) {
          e.preventDefault();
          const mobileDrawer = document.querySelector('[data-mobile-menu]');
          const mobileOverlay = document.querySelector('[data-mobile-menu-overlay]');
          if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
            mobileDrawer.classList.remove('is-open');
            if (mobileOverlay) mobileOverlay.classList.remove('is-active');
            document.body.classList.remove('mobile-menu-active');
          }
          this.openModal('search');
        }
      });

      // Close modal on close button, background click, or Escape key
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]')) {
          e.preventDefault();
          this.closeModal(e.target.closest('.modal'));
        } else if (e.target.classList && e.target.classList.contains('modal') && e.target.classList.contains('is-open')) {
          this.closeModal(e.target);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      if (searchInput && searchResults) {
        searchInput.addEventListener('input', this.debounce(async (e) => {
          const query = e.target.value.trim();

          if (query.length >= 3) {
            await this.performSearch(query, searchResults);
          } else {
            searchResults.innerHTML = '';
          }
        }, this.settings.debounceDelay));
      }

      if (searchForm) {
        searchForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const query = searchInput.value.trim();

          if (query) {
            window.location.href = `/search?q=${encodeURIComponent(query)}`;
          }
        });
      }
    },

    async performSearch(query, resultsContainer) {
      resultsContainer.innerHTML = '<div class="loading-spinner" style="margin: 2rem auto;"></div>';

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
        const data = await response.json();

        if (data.resources.results.products.length > 0) {
          resultsContainer.innerHTML = data.resources.results.products.map(product => {
            const imgUrl = (typeof product.featured_image === 'object' && product.featured_image ? product.featured_image.url : product.featured_image) || product.image || '';
            const price = this.formatMoney(product.price);
            return `
              <a href="${product.url}" class="search-result">
                ${imgUrl ? `<img src="${imgUrl}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
                <div class="search-result__details">
                  <h4>${escapeHtml(product.title)}</h4>
                  <p>${price}</p>
                </div>
              </a>
            `;
          }).join('') + `
            <div class="search-modal__view-all">
              <a href="/search?q=${encodeURIComponent(query)}" class="search-view-all-link">
                View all results for &quot;${escapeHtml(query)}&quot; &rarr;
              </a>
            </div>
          `;
        } else {
          const q = escapeHtml(query);
          const base = s('searchNoResults', 'No artworks found');
          resultsContainer.innerHTML = `<p class="no-results" style="padding: 1.5rem; text-align: center; color: rgba(250,245,229,0.7);">${base} ${q ? `for &quot;${q}&quot;` : ''}</p>`;
        }
      } catch (error) {
        console.error('Search error:', error);
        resultsContainer.innerHTML = `<p class="error" style="padding: 1.5rem; text-align: center; color: #ff6b6b;">${s('genericError', 'An error occurred. Please try again.')}</p>`;
      }
    },

    initNewsletter() {
      const newsletterForms = document.querySelectorAll('[data-newsletter-form]');

      newsletterForms.forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const email = form.querySelector('[type="email"]').value;
          const button = form.querySelector('[type="submit"]');
          const originalText = button.textContent;

          button.textContent = s('newsletterSubscribing', 'Subscribing...');
          button.disabled = true;

          try {
            const response = await fetch('/contact', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
              body: `form_type=customer&utf8=✓&contact[email]=${encodeURIComponent(email)}&contact[tags]=newsletter`
            });

            if (response.ok) {
              form.innerHTML = `<p class="success">${s('newsletterSuccess', 'Thank you for subscribing!')}</p>`;
            } else {
              throw new Error('Subscription failed');
            }
          } catch (error) {
            console.error('Newsletter error:', error);
            button.textContent = s('tryAgainError', 'Error - Please try again');

            setTimeout(() => {
              button.textContent = originalText;
              button.disabled = false;
            }, 3000);
          }
        });
      });
    },

    initWishlist() {
      const WISHLIST_KEY = 'prestige_wishlist';

      try {
        this.wishlist = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      } catch (err) {
        this.wishlist = [];
      }

      this.wishlistDrawer = document.querySelector('[data-wishlist-drawer]');

      this.updateWishlistButtons();
      this.updateWishlistCount();
      this.updateWishlistDrawer();

      document.addEventListener('click', (e) => {
        // 1. Wishlist Drawer Toggle (Header button or mobile menu button)
        if (e.target.closest('[data-wishlist-drawer-toggle]')) {
          e.preventDefault();
          this.toggleWishlistDrawer();
          return;
        }

        // 2. Wishlist Drawer Close (Close icon or backdrop overlay)
        if (e.target.closest('[data-wishlist-close]') || (e.target.matches && e.target.matches('[data-wishlist-overlay]'))) {
          e.preventDefault();
          this.closeWishlistDrawer();
          return;
        }

        // 3. Remove single item from Wishlist Drawer
        const drawerRemoveBtn = e.target.closest('[data-wishlist-drawer-remove]');
        if (drawerRemoveBtn) {
          e.preventDefault();
          const pId = String(drawerRemoveBtn.dataset.productId);
          const index = this.wishlist.findIndex(item => String(item.id) === pId);
          if (index > -1) {
            const removed = this.wishlist.splice(index, 1)[0];
            localStorage.setItem(WISHLIST_KEY, JSON.stringify(this.wishlist));
            this.updateWishlistButtons();
            this.updateWishlistCount();
            this.updateWishlistDrawer();
            this.updateWishlistPage();
            this.showNotification(`${removed.title || 'Artwork'} removed from wishlist`, 'info');
          }
          return;
        }

        // 4. Product Card Wishlist Toggle
        const toggleBtn = e.target.closest('[data-wishlist-toggle]');
        if (toggleBtn) {
          e.preventDefault();

          const productId = toggleBtn.dataset.productId;
          const productTitle = toggleBtn.dataset.productTitle;
          const productImage = toggleBtn.dataset.productImage;
          const productPrice = toggleBtn.dataset.productPrice;
          const productUrl = toggleBtn.dataset.productUrl;

          this.toggleWishlistItem({
            id: productId,
            title: productTitle,
            image: productImage,
            price: productPrice,
            url: productUrl
          });
          return;
        }
      });
    },

    toggleWishlistItem(product) {
      const index = this.wishlist.findIndex(item => String(item.id) === String(product.id));

      if (index > -1) {
        this.wishlist.splice(index, 1);
        this.showNotification(`${product.title} removed from wishlist`, 'info');
      } else {
        this.wishlist.push(product);
        this.showNotification(`${product.title} added to wishlist`, 'success');
      }

      localStorage.setItem('prestige_wishlist', JSON.stringify(this.wishlist));

      this.updateWishlistButtons();
      this.updateWishlistCount();
      this.updateWishlistPage();
      this.updateWishlistDrawer();
    },

    toggleWishlistDrawer() {
      if (this.wishlistDrawer) {
        if (this.wishlistDrawer.classList.contains('is-open')) {
          this.closeWishlistDrawer();
        } else {
          this.openWishlistDrawer();
        }
      }
    },

    openWishlistDrawer() {
      const mobileDrawer = document.querySelector('[data-mobile-menu]');
      const mobileOverlay = document.querySelector('[data-mobile-menu-overlay]');
      if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
        mobileDrawer.classList.remove('is-open');
        if (mobileOverlay) mobileOverlay.classList.remove('is-active');
        document.body.classList.remove('mobile-menu-active');
      }

      if (this.wishlistDrawer) {
        if (this.cartDrawer && this.cartDrawer.classList.contains('is-open')) {
          this.closeCart();
        }
        this.wishlistDrawer.classList.add('is-open');
        this.wishlistDrawer.setAttribute('aria-hidden', 'false');
        const overlay = document.querySelector('[data-wishlist-overlay]');
        if (overlay) overlay.classList.add('is-active');
        document.body.classList.add('wishlist-drawer-open');
        this.updateWishlistDrawer();
      }
    },

    closeWishlistDrawer() {
      if (this.wishlistDrawer) {
        this.wishlistDrawer.classList.remove('is-open');
        this.wishlistDrawer.setAttribute('aria-hidden', 'true');
        const overlay = document.querySelector('[data-wishlist-overlay]');
        if (overlay) overlay.classList.remove('is-active');
        document.body.classList.remove('wishlist-drawer-open');
      }
    },

    updateWishlistButtons() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
        const productId = String(button.dataset.productId);
        const isInWishlist = this.wishlist.some(item => String(item.id) === productId);

        button.classList.toggle('is-active', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);

        const label = button.querySelector('.wishlist-btn-label');
        if (label) {
          label.textContent = isInWishlist ? 'Saved in Wishlist' : 'Save to Wishlist';
        }
      });
    },

    updateWishlistCount() {
      const count = this.wishlist ? this.wishlist.length : 0;
      document.querySelectorAll('[data-wishlist-count]').forEach(element => {
        element.textContent = count;
        if (element.classList.contains('site-header__wishlist-count')) {
          element.style.display = count > 0 ? 'flex' : 'none';
        } else {
          element.style.display = 'inline';
        }
        element.classList.remove('badge-pulse');
        void element.offsetWidth;
        element.classList.add('badge-pulse');
      });
    },

    updateWishlistDrawer() {
      const drawerItems = document.querySelector('[data-wishlist-drawer-items]');
      if (!drawerItems) return;

      if (!this.wishlist || this.wishlist.length === 0) {
        drawerItems.innerHTML = `
          <div class="wishlist-drawer__empty">
            <div class="wishlist-drawer__empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" stroke-width="1.8">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <h3 class="wishlist-drawer__empty-title">Your Wishlist is Empty</h3>
            <p class="wishlist-drawer__empty-desc">Save your favorite artworks by clicking the heart icon on any artwork.</p>
            <a href="/collections/all" class="wishlist-drawer__browse-btn" data-wishlist-close>Explore Artworks</a>
          </div>
        `;
        return;
      }

      drawerItems.innerHTML = this.wishlist.map(product => `
        <div class="wishlist-drawer__item" data-product-id="${product.id}">
          <a href="${product.url}" class="wishlist-drawer__item-img">
            <img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy">
          </a>
          <div class="wishlist-drawer__item-info">
            <a href="${product.url}" class="wishlist-drawer__item-title">${escapeHtml(product.title)}</a>
            <div class="wishlist-drawer__item-price">${product.price}</div>
            <div class="wishlist-drawer__item-actions">
              <a href="${product.url}" class="wishlist-drawer__item-view-btn">View Artwork</a>
              <button type="button" class="wishlist-drawer__item-remove" data-wishlist-drawer-remove data-product-id="${product.id}" aria-label="Remove artwork">
                Remove
              </button>
            </div>
          </div>
        </div>
      `).join('');
    },

    updateWishlistPage() {
      const wishlistContainer = document.querySelector('[data-wishlist-items]');

      if (wishlistContainer) {
        if (this.wishlist.length > 0) {
          wishlistContainer.innerHTML = this.wishlist.map(product => `
            <div class="wishlist-item">
              <a href="${product.url}" class="wishlist-item__image">
                <img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy">
              </a>
              <div class="wishlist-item__details">
                <h3><a href="${product.url}">${escapeHtml(product.title)}</a></h3>
                <p class="wishlist-item__price">${product.price}</p>
                <button data-wishlist-toggle data-product-id="${product.id}">
                  ${s('wishlistRemove', 'Remove from wishlist')}
                </button>
              </div>
            </div>
          `).join('');
        } else {
          wishlistContainer.innerHTML = `<p>${s('wishlistEmpty', 'Your wishlist is empty')}</p>`;
        }
      }
    },

    initQuickShop() {
      document.addEventListener('click', async (e) => {
        const directAddBtn = e.target.closest('[data-direct-add-to-cart]');
        if (directAddBtn) {
          e.preventDefault();
          const variantId = directAddBtn.dataset.variantId;
          const originalContent = directAddBtn.innerHTML;
          directAddBtn.innerHTML = '<span>Adding...</span>';
          directAddBtn.disabled = true;

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });

            if (response.ok) {
              await this.updateCart();
              this.openCart();
            }
          } catch (err) {
            console.error('Direct add to cart error:', err);
          } finally {
            directAddBtn.innerHTML = originalContent;
            directAddBtn.disabled = false;
          }
          return;
        }

        const quickShopBtn = e.target.closest('[data-quick-shop]');
        if (quickShopBtn) {
          e.preventDefault();
          const productHandle = quickShopBtn.dataset.productHandle;
          await this.openQuickShop(productHandle);
        }
      });
    },

    async openQuickShop(handle) {
      const modal = document.querySelector('[data-quick-shop-modal]');

      if (!modal) return;

      const content = modal.querySelector('[data-quick-shop-content]');
      content.innerHTML = '<div class="loading-spinner"></div>';

      this.openModal('quick-shop');

      try {
        const response = await fetch(`/products/${handle}?view=quick-shop`);
        const html = await response.text();

        content.innerHTML = html;

        this.initProductForms();
        this.initProductMedia();
      } catch (error) {
        console.error('Quick shop error:', error);
        content.innerHTML = `<p class="error">${s('productLoadError', 'Failed to load product. Please try again.')}</p>`;
      }
    },

    initCollectionFilters() {
      const filterForm = document.querySelector('[data-filter-form]');
      const sortSelect = document.querySelector('[data-sort-select]');

      if (filterForm) {
        filterForm.addEventListener('change', this.debounce(() => {
          this.applyFilters(filterForm);
        }, this.settings.debounceDelay));

        filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.applyFilters(filterForm);
        });
      }

      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          const url = new URL(window.location);
          url.searchParams.set('sort_by', sortSelect.value);
          window.location = url.toString();
        });
      }

      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-filter-clear]')) {
          e.preventDefault();
          this.clearFilters();
        }

        if (e.target.matches('[data-filter-remove]')) {
          e.preventDefault();
          const filterId = e.target.dataset.filterId;
          this.removeFilter(filterId);
        }
      });
    },

    applyFilters(form) {
      const formData = new FormData(form);
      const params = new URLSearchParams(formData);

      const url = new URL(window.location);
      url.search = params.toString();

      this.loadFilteredProducts(url.toString());
    },

    clearFilters() {
      const url = new URL(window.location);
      url.search = '';

      this.loadFilteredProducts(url.toString());
    },

    removeFilter(filterId) {
      const url = new URL(window.location);
      url.searchParams.delete(filterId);

      this.loadFilteredProducts(url.toString());
    },

    async loadFilteredProducts(url) {
      const productsContainer = document.querySelector('[data-products-grid]');
      const loadingOverlay = document.querySelector('.loading-overlay');

      if (!productsContainer) return;

      loadingOverlay?.classList.add('is-loading');

      try {
        const response = await fetch(url);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newProducts = doc.querySelector('[data-products-grid]').innerHTML;
        const newFilters = doc.querySelector('[data-active-filters]')?.innerHTML;

        productsContainer.innerHTML = newProducts;

        const activeFilters = document.querySelector('[data-active-filters]');
        if (activeFilters && newFilters) {
          activeFilters.innerHTML = newFilters;
        }

        window.history.pushState({}, '', url);

        this.initAnimations();
      } catch (error) {
        console.error('Filter error:', error);
      } finally {
        loadingOverlay?.classList.remove('is-loading');
      }
    },

    initAnimations() {
      if (!window.IntersectionObserver) return;

      const animatedElements = document.querySelectorAll('[data-animate]');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(element => {
        observer.observe(element);
      });
    },

    initAccessibility() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          document.body.classList.add('keyboard-nav');
        }
      });

      document.addEventListener('mousedown', () => {
        document.body.classList.remove('keyboard-nav');
      });

      const skipLink = document.querySelector('.skip-to-content-link');
      if (skipLink) {
        skipLink.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.querySelector(skipLink.getAttribute('href'));
          if (target) {
            target.focus();
            window.scrollTo({
              top: target.offsetTop,
              behavior: 'smooth'
            });
          }
        });
      }
    },

    showNotification(message, type = 'success') {
      const oldNotification = document.querySelector('.notification');
      if (oldNotification) oldNotification.remove();

      const notification = document.createElement('div');
      notification.className = `notification notification--${type}`;
      notification.setAttribute('role', 'status');
      notification.setAttribute('aria-live', 'polite');
      notification.innerHTML = `
        <span class="notification__icon">${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
        <span class="notification__msg">${escapeHtml(message)}</span>
      `;

      document.body.appendChild(notification);

      requestAnimationFrame(() => {
        notification.classList.add('is-visible');
      });

      setTimeout(() => {
        notification.classList.remove('is-visible');

        setTimeout(() => {
          notification.remove();
        }, this.settings.animationDuration);
      }, 3000);
    },

    formatMoney(cents) {
      if (typeof cents === 'string') {
        if (cents.includes('.')) {
          cents = Math.round(parseFloat(cents) * 100);
        } else {
          cents = parseInt(cents, 10) || 0;
        }
      } else if (typeof cents !== 'number') {
        cents = 0;
      }
      const formatted = Math.round(cents / 100).toLocaleString('en-PK');
      const format = window.theme?.moneyFormat || 'Rs. {{amount}}';
      return format.replace(/\{\{\s*amount\w*\s*\}\}/g, formatted);
    },

    debounce(func, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    Prestige.init();
  });

  window.Prestige = Prestige;
  window.PrestigeTheme = Prestige;
})();